import { NgClass } from '@angular/common'
import { afterNextRender, ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, OnDestroy, output, signal, viewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MobileFilterDrawerService } from '@app/v2/shared/services/mobile-filter-drawer.service'

@Component({
  selector: 'app-mobile-filter-drawer',
  standalone: true,
  imports: [NgClass],
  templateUrl: './mobile-filter-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MobileFilterDrawerComponent implements OnDestroy {
  public readonly title = input('Filter by')
  public readonly closed = output<void>()
  public readonly reset = output<void>()
  public readonly apply = output<void>()

  public readonly drawerService = inject(MobileFilterDrawerService)
  public readonly isPanelActive = signal(false)

  private readonly _panel = viewChild.required<ElementRef<HTMLElement>>('panel')
  private _panelActivateFrame: number | null = null
  private _panelActivatePostFrame: number | null = null
  private _isDestroyed = false

  constructor() {
    this.drawerService.closed$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.closed.emit()
    })

    effect(() => {
      if (!this.drawerService.isOpen()) {
        this._cancelPanelActivateFrames()
        this.isPanelActive.set(false)
        return
      }

      this.isPanelActive.set(false)
      this._cancelPanelActivateFrames()

      this._panelActivateFrame = requestAnimationFrame(() => {
        this._panelActivateFrame = null

        this._panelActivatePostFrame = requestAnimationFrame(() => {
          this._panelActivatePostFrame = null

          if (!this._isDestroyed && this.drawerService.isOpen()) {
            this.isPanelActive.set(true)
          }
        })
      })
    })

    afterNextRender(() => {
      this.drawerService.registerHost(this._panel().nativeElement)
    })
  }

  ngOnDestroy(): void {
    this._isDestroyed = true
    this._cancelPanelActivateFrames()
    this.drawerService.unregisterHost()
  }

  public onClose(): void {
    this.drawerService.close()
  }

  public onReset(): void {
    this.reset.emit()
  }

  public onApply(): void {
    this.apply.emit()
  }

  /**
   * Cancels the panel activate frames.
   */
  private _cancelPanelActivateFrames(): void {
    if (this._panelActivateFrame !== null) {
      cancelAnimationFrame(this._panelActivateFrame)
      this._panelActivateFrame = null
    }

    if (this._panelActivatePostFrame !== null) {
      cancelAnimationFrame(this._panelActivatePostFrame)
      this._panelActivatePostFrame = null
    }
  }
}
